// Tenant health summary — super-admin sees all, org-admin sees own org,
// entity-admin sees own tenant. Hidden for viewer-tier roles.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings-context";
import { Building2, Users, Plug, Sparkles, ArrowUpRight } from "lucide-react";

function timeAgo(iso) {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `${Math.round(diff / 60)}m`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h`;
    return `${Math.round(diff / 86400)}d`;
}

export default function TenantHealthWidget() {
    const [items, setItems] = useState(null);
    const navigate = useNavigate();
    const { canAccess } = useSettings();
    const canOperations = canAccess("operations");
    useEffect(() => {
        api.get("/home/widget/tenant-health").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    }, []);
    return (
        <div className="border border-border rounded-sm bg-card p-4 h-full" data-testid="home-widget-tenant-health">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
                    <span className="eyebrow text-[10px]">Tenant health</span>
                </div>
                {canOperations && (
                    <button onClick={() => navigate("/operations")}
                            className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary"
                            data-testid="home-tenant-health-all">
                        Operations →
                    </button>
                )}
            </div>
            {items === null && <div className="text-xs text-muted-foreground">Loading…</div>}
            {items && items.length === 0 && (
                <div className="text-[12px] text-muted-foreground py-6 text-center">No tenants in scope.</div>
            )}
            {items && items.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border">
                            <tr>
                                <th className="text-left py-1.5 pr-3">Tenant</th>
                                <th className="text-right py-1.5 px-2"><Users className="h-3 w-3 inline" /></th>
                                <th className="text-right py-1.5 px-2"><Plug className="h-3 w-3 inline" /></th>
                                <th className="text-right py-1.5 px-2"><Sparkles className="h-3 w-3 inline" /> 7d</th>
                                <th className="text-right py-1.5 pl-2">Last sync</th>
                                <th className="w-4" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.slice(0, 8).map((t) => (
                                <tr key={t.id}
                                    onClick={canOperations ? () => navigate(`/operations?tenant=${t.id}`) : undefined}
                                    className={`border-b border-border last:border-b-0 group ${canOperations ? "cursor-pointer hover:bg-secondary/30" : "cursor-default"}`}
                                    data-testid={`home-tenant-row-${t.id}`}>
                                    <td className="py-2 pr-3 font-medium truncate max-w-[180px]">{t.name}</td>
                                    <td className="text-right px-2 font-mono">{t.active_users}</td>
                                    <td className="text-right px-2 font-mono">{t.connectors}</td>
                                    <td className="text-right px-2 font-mono">{t.ai_queries_7d}</td>
                                    <td className="text-right pl-2 font-mono text-muted-foreground">
                                        {t.last_sync_at ? `${timeAgo(t.last_sync_at)} ago` : "—"}
                                    </td>
                                    <td className="text-right">
                                        {canOperations && <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 inline" />}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
